import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, Lock, ShieldCheck, TriangleAlert } from 'lucide-react'
import AuthField from '../auth/AuthField'
import AuthHead from '../auth/AuthHead'
import AuthLayout from '../auth/AuthLayout'
import Button from '../../components/Button'
import PageLoader from '../../components/PageLoader'
import ResultDialog from '../../components/ResultDialog'
import { useResource } from '../../lib/useResource'
import { getResetPasswordPage, resetPassword } from './loginService'
import { strings as S } from './strings'

/**
 * Where a password-reset email lands: `/ResetPassword?username=…&token=…`.
 *
 * Built here rather than carried over — the legacy screen had no React equivalent (R5).
 * It is the same auth surface as login and signup, with the two fields the Razor form had.
 *
 * THE LINK CAN BE REFUSED before anything is typed: the feature can be switched off, the
 * username can be unknown. `GET /api/reset-password` says which, and the server's reason
 * is shown in place of the form rather than letting someone fill in a password that was
 * never going to be accepted.
 *
 * The username and token travel in the request body — see `resetPassword()` for why that
 * is a simplification rather than a change.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate()

  const [params] = useSearchParams()
  const username = params.get('username') ?? params.get('Username') ?? ''
  const token = params.get('token') ?? params.get('Token') ?? ''

  const { data: page, loading, error } = useResource(
    (signal) => getResetPasswordPage({ username, token }, signal),
    [username, token],
  )

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  function validate() {
    const next = {}
    if (password === '') next.password = S.messages.required
    if (confirm === '') next.confirm = S.messages.required
    else if (confirm !== password) next.confirm = S.messages.passwordMismatch
    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!validate()) return

    setSubmitting(true)
    /* The server holds the password policy - length, character classes, history - so a
       weak password is refused there and its reason is what the dialog shows. */
    const data = await resetPassword({
      username: page?.username || username,
      token: page?.token || token,
      password,
    })
    setSubmitting(false)

    setResult({ result: data.Result, message: data.Message })
  }

  /** A completed reset ends at the login form; a failed one stays put to be retried. */
  function handleResultClose() {
    const succeeded = result?.result
    setResult(null)
    if (succeeded) navigate('/login', { replace: true })
  }

  if (loading) {
    return (
      <AuthLayout>
        <div className="auth__card">
          <PageLoader />
        </div>
      </AuthLayout>
    )
  }

  if (error) {
    return (
      <AuthLayout>
        <div className="auth__card">
          <AuthHead title={S.resetPassword} subtitle={S.appTitle} />
          <div className="auth__body">
            <p className="auth__error" role="alert">
              <TriangleAlert size={16} aria-hidden="true" />
              <bdi>{error.message}</bdi>
            </p>
            <Button variant="soft" onClick={() => navigate('/login')}>
              {S.backToLogin}
            </Button>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <form className="auth__card" onSubmit={handleSubmit} noValidate>
        <AuthHead title={S.resetPassword} subtitle={page?.appTitle || S.appTitle} />

        <AuthField
          id="txtPassword"
          icon={Lock}
          label={S.newPassword}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={setPassword}
          error={errors.password}
          autoComplete="new-password"
          trailing={
            <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={S.newPassword}>
              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          }
        />

        <AuthField
          id="txtPasswordConfirm"
          icon={ShieldCheck}
          label={S.confirmPassword}
          type={showConfirm ? 'text' : 'password'}
          value={confirm}
          onChange={setConfirm}
          error={errors.confirm}
          autoComplete="new-password"
          trailing={
            <button type="button" onClick={() => setShowConfirm((v) => !v)} aria-label={S.confirmPassword}>
              {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          }
        />

        <Button type="submit" variant="primary" className="auth__submit" disabled={submitting}>
          {submitting ? S.submitting : S.resetPassword}
          {!submitting && <ArrowLeft className="ltr-flip" size={17} aria-hidden="true" />}
        </Button>

        <footer className="auth__foot">
          <a className="auth__footLink" href="/login">
            {S.backToLogin}
          </a>
        </footer>
      </form>

      <ResultDialog result={result} closeLabel={S.close} onClose={handleResultClose} />
    </AuthLayout>
  )
}
